import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';

import { useStripeSubscription } from 'src/api/queries';
import { SomethingWentWrong, Spinner } from 'src/components';
import { useStripeClient } from 'src/utils/stripe';

const StripeElements = ({
  children,
  options,
  publishableKey,
  error,
  isLoading = false,
}: React.PropsWithChildren & {
  options?: StripeElementsOptions;
  publishableKey?: string;
  error?: Error | null;
  isLoading?: boolean;
}) => {
  const stripe = useStripeClient(publishableKey);

  if (isLoading || stripe.isLoading) {
    return (
      <div className="flex size-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || stripe.isError || !stripe.data) {
    return <SomethingWentWrong error={error ?? stripe.error} />;
  }

  return (
    <Elements stripe={stripe.data} options={options}>
      {children}
    </Elements>
  );
};

export const WithStripeElements = ({
  children,
  options,
  publishableKey,
}: React.PropsWithChildren & {
  options?: StripeElementsOptions;
  publishableKey?: string;
}) => {
  if (publishableKey) {
    return (
      <StripeElements options={options} publishableKey={publishableKey}>
        {children}
      </StripeElements>
    );
  }

  return (
    <StoreStripeElements options={options}>{children}</StoreStripeElements>
  );
};

const StoreStripeElements = ({
  children,
  options,
}: React.PropsWithChildren & { options?: StripeElementsOptions }) => {
  const subscription = useStripeSubscription();

  return (
    <StripeElements
      options={options}
      publishableKey={subscription.data?.paymentInformation?.publishableKey}
      error={subscription.isError ? subscription.error : null}
      isLoading={subscription.isLoading}
    >
      {children}
    </StripeElements>
  );
};
